// Editing one role, without how it looks: Super Admin's drawer and the brand's
// own Roles and access page both render on top of this.

import { useState } from "react";
import { toast } from "sonner";
import { useSaveTenantRole } from "../../lib/api";
import {
  knownPermissions,
  roleCodeFrom,
  roleFormErrors,
  rolePayload,
  togglePermission,
  withLevel,
} from "../../lib/organization";

const detail = (e, fallback) => e?.response?.data?.detail || fallback;

export function useRoleForm(role, tenantId, onClose) {
  const save = useSaveTenantRole(tenantId);
  const isNew = !role.id;
  const [form, setForm] = useState({ ...role, permissions: knownPermissions(role) });
  const [codeTouched, setCodeTouched] = useState(!isNew);
  const [errors, setErrors] = useState({});

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
  };

  // The code follows the name until it is typed by hand.
  const setLabel = (text) => set({ label: text, ...(codeTouched ? {} : { code: roleCodeFrom(text) }) });

  const setCode = (text) => {
    setCodeTouched(true);
    set({ code: text.toLowerCase() });
  };

  const toggle = (id) => set({ permissions: togglePermission(form.permissions, id) });
  const setLevel = (id, level) => set({ permissions: withLevel(form.permissions, id, level) });

  const onSave = async () => {
    const found = roleFormErrors(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    try {
      await save.mutateAsync(rolePayload(form));
      toast.success(isNew ? "Role created." : "Role saved.");
      onClose();
    } catch (e) {
      toast.error(detail(e, "Save failed."));
    }
  };

  const onDelete = async () => {
    try {
      await save.mutateAsync({ id: form.id, remove: true });
      toast.success("Role deleted.");
      onClose(true);
    } catch (e) {
      toast.error(detail(e, "Delete failed."));
    }
  };

  return {
    form,
    set,
    errors,
    isNew,
    codeLocked: form.is_system,
    setLabel,
    setCode,
    toggle,
    setLevel,
    onSave,
    onDelete,
    pending: save.isPending,
  };
}
